import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ContentChild,
    ElementRef,
    EventEmitter,
    Input,
    NgZone,
    OnDestroy,
    Output,
    Renderer2,
    TemplateRef
} from "@angular/core";
import type {ItemDisplayInfo} from "../item-display-info";
import {
    ColorEditControlComponent
} from "../../../../../framework/common-controls/edit-controls/color-edit-control/color-edit-control.component";
import {
    FontSingleSelectEditControlComponent
} from "../../../../../framework/common-controls/edit-controls/font-single-select-edit-control/font-single-select-edit-control.component";
import {
    FontStyleSingleSelectEditControlComponent
} from "../../../../../framework/common-controls/edit-controls/font-style-single-select-edit-control/font-single-select-edit-control.component";
import {DomSanitizer, SafeResourceUrl} from "@angular/platform-browser";
import {ScreenLayoutEditorModel} from "../screen-layout-editor.model";

/**
 * Button displayed on a screen layout
 */
@Component({
    selector: "wbo-screen-layout-button",
    templateUrl: "./screen-layout-button.component.html",
    styleUrls: ["./screen-layout-button.component.scss"]
})
export class ScreenLayoutButtonComponent implements AfterViewInit, OnDestroy {
    @Input() item: ItemDisplayInfo;

    @Input() selected: boolean;

    /**
     * indicates that the button is an action button instead of a touch item
     */
    @Input() actionButton: boolean;

    @Input() icon: string;

    @Input() title: string;

    @Input() image: string;

    @Input() showLeftCommands: boolean;
    @Input() showRightCommands: boolean;
    @Input() leftCommandsToggled: boolean;
    @Input() rightCommandsToggled: boolean;

    @Input() showNavigateTo: boolean;

    @ContentChild("leftCommands", {read: TemplateRef}) public leftCommands: TemplateRef<HTMLTemplateElement>;
    @ContentChild("rightCommands", {read: TemplateRef}) public rightCommands: TemplateRef<HTMLTemplateElement>;

    @Output() navigated: EventEmitter<any> = new EventEmitter<any>();

    hover: boolean = false;

    private tearDownDragOverListenerFn: Function;

    constructor(private sanitizer: DomSanitizer,
                public model: ScreenLayoutEditorModel,
                protected element: ElementRef,
                protected ngZone: NgZone,
                private changeDetectorRef: ChangeDetectorRef,
                private renderer: Renderer2) {
    }

    public get imageUrl(): SafeResourceUrl {
        return this.sanitizer.bypassSecurityTrustResourceUrl("data:image/png;base64," + this.image);
    }

    get colour(): string {
        return this.item ? ColorEditControlComponent.toColorHex(this.item.colour) : undefined;
    }

    get foreColour(): string {
        return this.item ? ColorEditControlComponent.toColorHex(this.item.foreColour) : undefined;
    }

    get font(): string[] {
        return this.item ? FontSingleSelectEditControlComponent.mapFontFamily(this.item.fontName) : undefined;
    }

    get fontSize(): string {
        return this.item && this.item.fontSize ? this.item.fontSize + "px" : undefined;
    }

    get fontStyle(): string {
        return !this.item ? undefined
            : this.item.fontStyle === FontStyleSingleSelectEditControlComponent.ITALIC || this.item.fontStyle === FontStyleSingleSelectEditControlComponent.ITALIC_BOLD ? "italic"
            : "normal";
    }

    get fontWeight(): string {
        return !this.item ? undefined
            : this.item.fontStyle === FontStyleSingleSelectEditControlComponent.BOLD || this.item.fontStyle === FontStyleSingleSelectEditControlComponent.ITALIC_BOLD ? "bold"
                : this.item.fontStyle === FontStyleSingleSelectEditControlComponent.PLAIN || this.item.fontStyle === FontStyleSingleSelectEditControlComponent.ITALIC ? "normal"
                    : undefined;
    }

    get text(): string {
        if (this.title) {
            return this.title;
        }

        if (this.item) {
            return this.item.displayName;
        }

        return "";
    }

    get showLeftCommandElement(): boolean {
        return this.showLeftCommands && !this.model.dragging && (this.hover || this.leftCommandsToggled);
    }

    get showRightCommandElement(): boolean {
        return this.showRightCommands && !this.model.dragging && (this.hover || this.rightCommandsToggled);
    }

    get showNavigateToElement(): boolean {
        return this.showNavigateTo && !this.model.dragging && this.hover;
    }

    ngAfterViewInit(): void {
        this.ngZone.runOutsideAngular(() => {
            this.tearDownDragOverListenerFn = this.renderer.listen(this.element.nativeElement,
                "dragover",
                (event: DragEvent) => {
                    event["targetItem"] = this.item;
                });
        });
    }

    mouseEnter(): void {
        this.hover = true;
        // when dragging to another page and cancelling dropping the hover is not being detected without the detectChanges
        this.changeDetectorRef.detectChanges();
    }

    mouseLeave(): void {
        this.hover = false;
        // when dragging to another page and cancelling dropping the hover is not being detected without the detectChanges
        this.changeDetectorRef.detectChanges();
    }

    stopPropagation(event: Event) {
        event.stopPropagation()
    }

    navigate(event: Event): void {
        this.navigated.emit();
        event.stopPropagation();
    }

    ngOnDestroy(): void {
        this.tearDownDragOverListenerFn();
    }
}
